import { useIndividualStore } from '@/features/individual/store/individual.store'
import { useJuridicalStore } from '@/features/juridical/store/juridical.store'
import { Actions } from '@/features/workflows/lib/consts'
import {
  EnumPersonClient,
  TYPE_PERSON_CLIENT,
  type TypePersonClient
} from '@/shared/catalogs/type-person'

import { CLIENT_SCHEMA, type IClient } from '../models/client-schema'
import type { ClientV2Dossier } from '../services/clients-admin-v2.service'
import { useClientStore } from '../store/client.store'
import { useProductsStore } from '../store/products.store'
import { useSignatoriesStore } from '../store/signatories.store'
import {
  toDossierCard,
  toDossierOriginal,
  toFormEquitySection,
  toFormSection
} from './client-dossier-mapper'

/**
 * Coordinator de los stores de cliente (deep module, Ousterhout).
 *
 * Crear un cliente exige orquestar 5 stores (client + individual + juridical +
 * products + signatories). Esa coordinación vivía duplicada en cada caller
 * (modal de alta, wizard), así que olvidar un store dejaba el form sin montar
 * —el bug del wizard—. Acá la coordinación vive en UN solo lugar: los callers
 * piden "empezar a crear" y el coordinator esconde el "qué resetear/inicializar".
 *
 * Information hiding: los stores individuales son detalle interno; los flujos de
 * creación/edición no deberían tocarlos directo.
 */

/** Tipo de persona aceptado: el literal o el enum (mismo string en runtime). */
type PersonInput = TypePersonClient | EnumPersonClient

export interface StartCreateInput {
  typePerson: PersonInput
  advisorId?: string | null
}

/**
 * Normaliza el tipo de persona al enum interno. El modal de alta tipa el valor
 * como `EnumPersonClient` y el wizard como literal `'individual' | 'juridical'`:
 * el mismo string en runtime, dos tipos distintos. Comparamos sobre el string
 * para reconciliar el boundary sin casts ni narrowing engañoso.
 */
function normalizeTypePerson(value: PersonInput): EnumPersonClient {
  return String(value) === TYPE_PERSON_CLIENT.JURIDICAL_PERSON
    ? EnumPersonClient.JURIDICAL_PERSON
    : EnumPersonClient.INDIVIDUAL_PERSON
}

/**
 * Reset atómico de TODOS los stores de cliente. Imposible olvidar uno
 * (define errors out of existence).
 */
export function resetAll(): void {
  useClientStore.getState().clearData()
  useIndividualStore.getState().clearData()
  useJuridicalStore.getState().clearData()
  useProductsStore.getState().setProducts([])
  useSignatoriesStore.getState().setSignatories([])
}

/**
 * Inicializa los stores para crear un cliente nuevo: limpia todo, marca la
 * acción `create` y siembra el cliente con tipo de persona y asesor.
 * Tras esto, el form (individual/jurídico) monta con el estado que necesita.
 *
 * `groupId` estuvo acá hasta el 2026-08-11 y se retiró con el campo que lo
 * alimentaba (decisión Nº 31): el único llamador que lo pasaba era el modal de
 * alta, y su valor SIEMPRE viajaba vacío porque el catálogo que lo llenaba
 * —`GET /api/v1/groups/active`— no existe en ninguna versión de la API.
 * `CLIENT_SCHEMA.GROUP` sigue vivo a propósito: el grupo ES un atributo real
 * del cliente y los routers del FEIC lo leen al montar un expediente. Lo que se
 * retiró es poder ELEGIRLO en el alta, no el concepto.
 */
export function startCreate(input: StartCreateInput): void {
  const typePerson = normalizeTypePerson(input.typePerson)

  resetAll()
  useClientStore.getState().setAction([Actions.create])

  const client: Partial<IClient> = { [CLIENT_SCHEMA.TYPE_PERSON]: typePerson }
  if (input.advisorId) client[CLIENT_SCHEMA.ADVISOR] = input.advisorId

  useClientStore.getState().setClient(client)
}

/**
 * Monta los stores desde un expediente v2, para editarlo o para verlo.
 *
 * Es el reverso exacto de `startCreate`: una sola puerta que sabe QUÉ store
 * recibe cada parte del expediente. Los callers —los cuatro routers del FEIC—
 * piden «cargá este expediente» y no tocan ningún store directo; ese
 * information hiding es lo que evitó el bug del alta, donde olvidarse de montar
 * un store dejaba el formulario a medio armar.
 *
 * La rama que no corresponde a `typePerson` se limpia en vez de dejarse como
 * estaba: un expediente jurídico abierto después de uno individual heredaba los
 * datos del anterior, y el formulario los mostraba como si fueran suyos.
 */
export function loadDossier(dossier: ClientV2Dossier, action: Actions): void {
  resetAll()

  const store = useClientStore.getState()
  store.setAction([action])
  store.setClient(toDossierCard(dossier) as unknown as Partial<IClient>)
  store.setClientOriginal(toDossierOriginal(dossier) as never)

  if (dossier.typePerson === EnumPersonClient.INDIVIDUAL_PERSON) {
    const individual = useIndividualStore.getState()
    const person = dossier.individual
    if (person?.applicant) individual.setApplicant(toFormSection(person.applicant) as never)
    if (person?.pep) individual.setPep(toFormSection(person.pep) as never)
    if (person?.economicInformation)
      individual.setEconomicForm(toFormSection(person.economicInformation) as never)
    individual.setEquityStatement(toFormEquitySection(person?.equityStatement ?? null) as never)
    individual.setLegalRepresentatives(toFormSection(person?.legalRepresentatives ?? []) as never)
    individual.setPhoto(person?.photo ?? null)
    individual.setRenapVerification((person?.renapVerification ?? null) as never)
    return
  }

  const juridical = useJuridicalStore.getState()
  const company = dossier.juridical
  if (company?.applicant) juridical.setApplicant(toFormSection(company.applicant) as never)
  if (company?.economicInformation)
    juridical.setEconomicForm(toFormSection(company.economicInformation) as never)
  juridical.setEquityStatement(toFormEquitySection(company?.equityStatement ?? null) as never)
  juridical.setLegalRepresentatives(toFormSection(company?.legalRepresentatives ?? []) as never)
}
